import React from 'react';

// Compact colour picker + swatches used on the Invoice Design → Order Email tab.
// Each control renders a native colour input plus a hex text field, plus a
// "DARK"/"LIGHT" quick-preset toggle that snaps all colours to a coherent theme.

const LIGHT_PRESET = {
  email_bg_color: '#f4f4f5',
  email_card_bg: '#ffffff',
  email_summary_bg: '#fafafa',
  email_footer_bg: '#f4f4f5',
  email_heading_color: '#111114',
  email_body_color: '#4b5563',
  email_muted_color: '#dd1f26',
  email_footer_color: '#9ca3af',
  email_border_color: '#e5e7eb',
};

const CONTROLS = [
  { key: 'email_bg_color', label: 'PAGE BACKGROUND', hint: 'Behind the main card' },
  { key: 'email_card_bg', label: 'CARD BACKGROUND', hint: 'Main email card' },
  { key: 'email_summary_bg', label: 'SUMMARY BOX BG', hint: 'Order summary + invoice box' },
  { key: 'email_footer_bg', label: 'FOOTER BACKGROUND', hint: 'Footer strip' },
  { key: 'email_heading_color', label: 'HEADING TEXT', hint: 'Main heading + greeting + values' },
  { key: 'email_body_color', label: 'BODY TEXT', hint: 'Intro / closing paragraphs' },
  { key: 'email_muted_color', label: 'MUTED LABELS', hint: 'Eyebrows + row labels' },
  { key: 'email_footer_color', label: 'FOOTER TEXT', hint: 'Address + VAT line' },
  { key: 'email_border_color', label: 'BORDERS / DIVIDERS', hint: 'Section + row separators' },
];

export default function EmailColorControls({ settings, set }) {
  const isPreset = (preset) =>
    CONTROLS.every((c) => String(settings[c.key] || '').toLowerCase() === preset[c.key]);

  return (
    <div className="border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-white/40 text-[10px] font-bold tracking-widest">EMAIL COLOURS</p>
        <button
          type="button"
          onClick={() => CONTROLS.forEach((c) => set(c.key, LIGHT_PRESET[c.key]))}
          disabled={isPreset(LIGHT_PRESET)}
          className={`text-[10px] font-bold tracking-widest px-3 py-1.5 border transition-colors ${
            isPreset(LIGHT_PRESET)
              ? 'border-primary/40 bg-primary/10 text-primary cursor-default'
              : 'border-white/15 text-white/50 hover:text-white hover:border-white/30'
          }`}
        >
          LIGHT PRESET
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {CONTROLS.map((c) => (
          <ColorRow
            key={c.key}
            label={c.label}
            hint={c.hint}
            value={settings[c.key] || '#000000'}
            onChange={(v) => set(c.key, v)}
          />
        ))}
      </div>
      <p className="text-white/20 text-[10px] mt-3 leading-relaxed">
        The accent colour ({settings.accent_color || '#dd1f26'}) is shared with the invoice and controls the top stripe, eyebrow text, summary-box border and the "Invoice Total" value. Click a preset to reset the whole palette at once.
      </p>
    </div>
  );
}

function ColorRow({ label, hint, value, onChange }) {
  const isLight = (value || '').toLowerCase() === '#ffffff';
  return (
    <div className="flex items-center gap-3 border border-white/10 bg-[#0d0d0f] px-3 py-2.5">
      <label
        className="relative w-9 h-9 border-2 border-white/15 hover:border-white/40 cursor-pointer shrink-0 overflow-hidden"
        style={{ background: value }}
        title={label}
      >
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>
      <div className="min-w-0 flex-1">
        <p className="text-white/70 text-[10px] font-bold leading-tight break-words">{label}</p>
        <p className="text-white/30 text-[9px] mt-0.5 leading-tight break-words">{hint}</p>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-white/60 font-mono text-[11px] mt-1 outline-none border-b border-transparent focus:border-primary/40"
        />
      </div>
    </div>
  );
}